import { useQuery } from '@tanstack/react-query';
import { Check, ExternalLink, Loader2, Link2, ArrowRight } from 'lucide-react';
import { gscApi } from '@/lib/api';

interface GscStepProps {
  onConnect: () => void;
  onSkip: () => void;
}

export function GscStep({ onConnect, onSkip }: GscStepProps) {
  const { data: gscStatus, isLoading } = useQuery({
    queryKey: ['gsc-status'],
    queryFn: gscApi.status,
  });

  const isConnected = gscStatus?.connected;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-semibold mb-2">Connect Google Search Console</h2>
      <p className="text-slate-600 mb-6">
        Connect GSC to automatically submit your sitemaps when you generate or update them.
      </p>

      {isConnected ? (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
          <div className="flex items-center gap-2 text-green-700 font-medium mb-2">
            <Check className="w-5 h-5" />
            Google Search Console Connected
          </div>
          <p className="text-sm text-green-600">
            Your sitemaps will be automatically submitted to GSC when you publish them.
          </p>
        </div>
      ) : (
        <div className="space-y-4 mb-6">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Link2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800 mb-1">Why connect GSC?</p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Sitemaps auto-submitted when you publish</li>
                  <li>• Faster indexing of your pages</li>
                  <li>• No manual sitemap submission needed</li>
                </ul>
              </div>
            </div>
          </div>

          <button
            onClick={onConnect}
            className="w-full py-3 bg-white border-2 border-slate-200 rounded-lg font-medium hover:border-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Connect Google Search Console
          </button>
        </div>
      )}

      <button
        onClick={onSkip}
        className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 flex items-center justify-center gap-2"
      >
        {isConnected ? 'Continue to Dashboard' : 'Skip for Now'}
        <ArrowRight className="w-4 h-4" />
      </button>

      {!isConnected && (
        <p className="text-xs text-slate-500 text-center mt-3">
          You can always connect GSC later from Settings.
        </p>
      )}
    </div>
  );
}
